import React, { useCallback, useEffect, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';

import { Popup } from '@atlaskit/popup/popup';
import type { TriggerProps } from '@atlaskit/popup/types';
import { Box } from '@atlaskit/primitives/compiled';
import { useProductDependencies } from '@atlassian/company-hub-common';
import { KeyboardHighlightProvider } from '@atlassian/search-dialog/search-keyboard';

import { SearchDropdown } from './search-dropdown';
import { SearchTextField } from './search-textfield';

export type ArticleSearchComponentProps = { isEditor: boolean; placeholderText?: string };

export const ArticleSearchComponent = ({
	isEditor,
	placeholderText,
}: ArticleSearchComponentProps): React.JSX.Element => {
	const [queryWord, setQueryWord] = useState<string>('');
	const [showDropdown, setShowDropdown] = useState(false);
	const inputBoxRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const [_, setInputWidth] = useState(inputBoxRef.current?.offsetWidth);

	const { cloudId, helpCenterAri } = useProductDependencies();

	useEffect(() => {
		const listener = () => setInputWidth(inputBoxRef.current?.offsetWidth);
		const unbind = bind(window, {
			type: 'resize',
			listener,
		});
		return unbind;
	}, []);

	const closeDropdown = () => {
		if (showDropdown) {
			setShowDropdown(false);
		}
	};

	const trigger = useCallback(
		(triggerProps: TriggerProps) => {
			const handleInput = (searchInput: string) => {
				setQueryWord(searchInput);
				setShowDropdown(searchInput.trim().length > 0);
			};

			return (
				<Box ref={inputBoxRef}>
					<SearchTextField
						keyboardRef={inputRef}
						{...triggerProps}
						ref={triggerProps.ref}
						onInput={handleInput}
						isReadOnly={isEditor}
						placeholderText={placeholderText}
						showDropdown={showDropdown}
						queryWord={queryWord}
					/>
				</Box>
			);
		},
		[isEditor, placeholderText, showDropdown, queryWord],
	);

	return (
		<Popup
			shouldReturnFocus={false}
			content={(contentProps) => {
				return (
					<KeyboardHighlightProvider listenerNode={inputRef?.current}>
						<SearchDropdown
							{...contentProps}
							cloudId={cloudId}
							helpCenterAri={helpCenterAri || ''}
							inputWidth={inputBoxRef.current?.offsetWidth || 0}
							queryWord={queryWord}
						/>
					</KeyboardHighlightProvider>
				);
			}}
			isOpen={showDropdown}
			autoFocus={false}
			placement="bottom-start"
			trigger={trigger}
			onClose={closeDropdown}
		/>
	);
};
